import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText, MapPin, CheckCircle2, AlertCircle, ArrowRight, ExternalLink, Sparkles } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { usePageTitle } from "@/lib/seo";

// Same provincial snapshot as the PDF, kept short for the public preview.
const PROVINCE_ROWS = [
  ["BC",  "$2,520", "+6.4%", "0.9%"],
  ["AB",  "$1,640", "+8.2%", "2.4%"],
  ["SK",  "$1,210", "+4.1%", "2.9%"],
  ["MB",  "$1,290", "+3.7%", "3.0%"],
  ["ON",  "$2,180", "+5.1%", "1.9%"],
  ["QC",  "$1,360", "+7.3%", "1.5%"],
  ["NB",  "$1,310", "+6.8%", "2.0%"],
  ["NS",  "$1,690", "+5.6%", "1.7%"],
  ["PEI", "$1,420", "+4.9%", "1.2%"],
  ["NL",  "$1,180", "+3.2%", "3.6%"],
  ["YT",  "$1,720", "+5.8%", "1.4%"],
  ["NT",  "$2,140", "+4.0%", "4.1%"],
  ["NU",  "$2,610", "+6.2%", "1.8%"],
];

const KEY_FINDINGS = [
  {
    title: "Vacancy below 2% nationally for the first time since 1988",
    body: "The 2025 CMHC Rental Market Survey put national vacancy at 1.8% — the tightest the country has been in 37 years.",
  },
  {
    title: "Prairies overtook BC/ON in rent growth",
    body: "Calgary, Edmonton, Saskatoon and Regina led the country in YoY rent increases, while Vancouver and Toronto decelerated for the first time since 2017.",
  },
  {
    title: "Insurance is the new property tax",
    body: "Property insurance is up 22-30% YoY across Canada. Tenant insurance verification has moved from paperwork to board-level decision.",
  },
  {
    title: "Smart triage cuts emergency dispatch ~40%",
    body: "Early adopters report a 38-44% reduction in after-hours emergency calls when an automated triage layer screens incoming maintenance tickets first.",
  },
];

const REPORT_PDF_URL = `${process.env.REACT_APP_BACKEND_URL || ""}/api/report/landlord-2026-canada/preview.pdf`;
const REPORT_REQUEST_URL = `${process.env.REACT_APP_BACKEND_URL || ""}/api/report/landlord-2026-canada/request`;

function StatPill({ value, label }) {
  return (
    <div className="bg-white border border-emerald-100 rounded-2xl px-5 py-4">
      <div className="text-2xl font-extrabold font-display text-emerald-700">{value}</div>
      <div className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mt-1">{label}</div>
    </div>
  );
}

export default function LandlordReport() {
  usePageTitle("The 2026 Canadian Landlord Report · Free PDF");
  const [form, setForm] = useState({ name: "", email: "", province: "", properties: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    const name = form.name.trim();
    const email = form.email.trim();
    if (!name || !email) { setErr("Name and email are required."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(REPORT_REQUEST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name, email,
          role: "landlord",
          province: form.province || null,
          properties: form.properties ? Number(form.properties) : null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Could not deliver the report.");
      setDone(true);
    } catch (e) {
      setErr(e.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white text-slate-900 min-h-screen" data-testid="landlord-report-page">
      <PublicHeader />

      <main>
        {/* HERO */}
        <section className="relative overflow-hidden border-b border-slate-100">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-sky-50 pointer-events-none"/>
          <div className="relative max-w-6xl mx-auto px-6 md:px-8 pt-16 md:pt-20 pb-12 md:pb-16">
            <div className="grid lg:grid-cols-5 gap-10 items-center">
              <div className="lg:col-span-3">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 border border-emerald-200 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">
                  <Sparkles className="w-3 h-3"/> 2026 · Free download
                </div>
                <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mt-5 text-slate-950">
                  The 2026 Canadian<br/>
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 via-teal-500 to-sky-500">Landlord Report</span>
                </h1>
                <p className="mt-5 text-lg text-slate-700 max-w-2xl leading-relaxed">
                  Rent, regulation and returns across all 13 provinces and territories. CMHC data, CRA tax moves, and the operational shifts shaping the year — in one 20+ page PDF.
                </p>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <a href={REPORT_PDF_URL} target="_blank" rel="noopener"
                    data-testid="report-preview-download"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold transition-colors shadow-lg shadow-emerald-500/20">
                    <Download className="w-4 h-4"/> Download the PDF now
                  </a>
                  <a href="#email-version"
                    data-testid="report-email-jump"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white border-2 border-slate-200 hover:border-emerald-400 text-slate-900 font-bold transition-colors">
                    Email me a copy <ArrowRight className="w-4 h-4"/>
                  </a>
                </div>
                <p className="mt-5 text-xs text-slate-500 max-w-xl">
                  Free. No paywall. No login. Source: CMHC Rental Market Survey (Jan 2026), Statistics Canada, CRA T776 guide, provincial RTA bodies.
                </p>
              </div>

              <div className="lg:col-span-2">
                <div className="bg-white rounded-3xl shadow-2xl shadow-emerald-900/10 border border-slate-100 overflow-hidden">
                  <div className="bg-gradient-to-br from-emerald-700 to-teal-600 px-7 py-8 text-white">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">Published by</div>
                    <div className="text-lg font-bold mt-1">Central Rentals Canada</div>
                    <div className="text-3xl font-display font-extrabold mt-6 leading-tight">2026<br/>Landlord<br/>Report</div>
                    <div className="text-xs uppercase tracking-[0.16em] font-bold opacity-75 mt-5">v1.0 · February 2026</div>
                  </div>
                  <div className="p-6 space-y-2.5">
                    <div className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-none"/> Provincial rent & vacancy tables</div>
                    <div className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-none"/> RTA notice rules · all 13 provinces</div>
                    <div className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-none"/> Six high-leverage CRA tax moves</div>
                    <div className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-none"/> 2026 outlook · six shifts to plan for</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* KEY FINDINGS */}
        <section className="max-w-6xl mx-auto px-6 md:px-8 py-14 md:py-20">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-700">What's inside</div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold mt-2 text-slate-950">Four findings reshaping the 2026 portfolio</h2>
          <div className="grid md:grid-cols-2 gap-5 mt-9">
            {KEY_FINDINGS.map((f, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-emerald-200 hover:shadow-lg hover:shadow-emerald-900/5 transition-all" data-testid={`finding-${i}`}>
                <div className="text-emerald-700 text-2xl font-display font-extrabold leading-none">0{i+1}</div>
                <h3 className="font-display text-lg font-bold mt-3 text-slate-900">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mt-2">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* PROVINCIAL DATA */}
        <section className="bg-slate-50 border-y border-slate-100">
          <div className="max-w-6xl mx-auto px-6 md:px-8 py-14 md:py-20">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-700">Provincial preview</div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold mt-2 text-slate-950">Average 2-BR rent · YoY · vacancy</h2>
            <p className="mt-3 text-slate-600 max-w-2xl">Source: CMHC RMS January 2026 · purpose-built rental, two-bedroom unit, calendar 2025. The full PDF includes urban/rural splits and city-level breakouts.</p>
            <div className="mt-8 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              <StatPill value="1.8%" label="National vacancy rate"/>
              <StatPill value="$1,850" label="National avg 2-BR rent"/>
              <StatPill value="+5.4%" label="YoY rent growth"/>
              <StatPill value="$1,430" label="Spread high-to-low province"/>
            </div>

            <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
              <table className="w-full text-sm" data-testid="province-table">
                <thead className="bg-emerald-700 text-white text-left">
                  <tr>
                    <th className="px-5 py-3 font-semibold text-xs uppercase tracking-wider">Province</th>
                    <th className="px-5 py-3 font-semibold text-xs uppercase tracking-wider">Avg 2-BR rent</th>
                    <th className="px-5 py-3 font-semibold text-xs uppercase tracking-wider">YoY</th>
                    <th className="px-5 py-3 font-semibold text-xs uppercase tracking-wider">Vacancy</th>
                  </tr>
                </thead>
                <tbody>
                  {PROVINCE_ROWS.map(([prov, rent, yoy, vac], i) => (
                    <tr key={prov} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                      <td className="px-5 py-3 font-bold text-slate-900"><MapPin className="w-3.5 h-3.5 inline mr-1.5 text-emerald-600"/>{prov}</td>
                      <td className="px-5 py-3 font-semibold text-slate-900">{rent}</td>
                      <td className="px-5 py-3 text-emerald-700 font-semibold">{yoy}</td>
                      <td className="px-5 py-3 text-slate-700">{vac}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-slate-500">Data summarized for the public preview. The full PDF cites every figure with its source URL.</p>
          </div>
        </section>

        {/* EMAIL FORM */}
        <section id="email-version" className="max-w-6xl mx-auto px-6 md:px-8 py-14 md:py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-700">Email me a copy</div>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold mt-2 text-slate-950">Save it for later — and the Q2 update</h2>
              <p className="mt-4 text-slate-700 leading-relaxed">Get the PDF in your inbox, plus a single email next quarter when the Q2 CMHC data drops. No spam, one-click unsubscribe at the bottom of every email.</p>
              <ul className="mt-6 space-y-3 text-sm text-slate-700">
                <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-600 flex-none mt-0.5"/> 20+ pages, fully sourced</li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-600 flex-none mt-0.5"/> Provincial breakouts you can hand your accountant</li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-600 flex-none mt-0.5"/> Plain-language CRA + RTA references</li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-600 flex-none mt-0.5"/> Free Canadian-built · no upsell, no paywall</li>
              </ul>
              <p className="mt-6 text-xs text-slate-500">
                Already have a copy? <a href={REPORT_PDF_URL} target="_blank" rel="noopener" className="text-emerald-700 underline">Download the latest PDF directly</a>.
              </p>
            </div>

            <div className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-emerald-900/5 p-7 md:p-9">
              {done ? (
                <div className="text-center py-10" data-testid="report-form-done">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-8 h-8 text-emerald-600"/>
                  </div>
                  <h3 className="font-display text-2xl font-extrabold mt-5 text-slate-950">Check your inbox</h3>
                  <p className="text-slate-600 mt-3 max-w-sm mx-auto">The full PDF is on its way to <b className="text-slate-900">{form.email}</b>. It usually arrives within 1-2 minutes.</p>
                  <a href={REPORT_PDF_URL} target="_blank" rel="noopener" className="inline-flex items-center gap-2 mt-6 text-emerald-700 font-semibold hover:underline">
                    Or download it right now <Download className="w-4 h-4"/>
                  </a>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="report-form">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-5 h-5 text-emerald-600"/>
                    <h3 className="font-display text-xl font-bold text-slate-950">Get the full report</h3>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Your name <span className="text-red-500">*</span></label>
                    <input required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}
                      placeholder="Avery Singh"
                      data-testid="report-form-name"
                      className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none"/>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1.5 block flex items-center gap-1.5"><span>Email</span><span className="text-rose-600 font-bold ml-0.5">*</span></label>
                    <input required type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}
                      placeholder="you@example.com"
                      data-testid="report-form-email"
                      className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none"/>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Province</label>
                      <select value={form.province} onChange={(e)=>setForm({...form,province:e.target.value})}
                        data-testid="report-form-province"
                        className="w-full border border-slate-300 rounded-lg px-3 py-3 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none">
                        <option value="">— Optional —</option>
                        {["BC","AB","SK","MB","ON","QC","NB","NS","PEI","NL","YT","NT","NU"].map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 block"># Properties</label>
                      <input type="number" min="0" max="9999" value={form.properties} onChange={(e)=>setForm({...form,properties:e.target.value})}
                        placeholder="3"
                        data-testid="report-form-properties"
                        className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none"/>
                    </div>
                  </div>

                  {err && (
                    <div className="flex items-start gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 text-xs" data-testid="report-form-error">
                      <AlertCircle className="w-4 h-4 flex-none mt-0.5"/>{err}
                    </div>
                  )}

                  <button type="submit" disabled={submitting}
                    data-testid="report-form-submit"
                    className="w-full mt-2 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold transition-colors shadow-lg shadow-emerald-500/20">
                    {submitting ? "Sending…" : (<>Send me the PDF <ArrowRight className="w-4 h-4"/></>)}
                  </button>
                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    By submitting this form you consent to receive the report and one Q2-update email from Central Rentals Canada (CASL-compliant). One-click unsubscribe in every message.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* BACKLINK FRIENDLY CTA */}
        <section className="bg-slate-950 text-white">
          <div className="max-w-5xl mx-auto px-6 md:px-8 py-14 md:py-16 text-center">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold">Run your portfolio on the same data.</h2>
            <p className="mt-4 text-slate-300 max-w-2xl mx-auto">
              Central Rentals Canada is the property platform built specifically for Canadian landlords — provincial leases, Stripe Connect rent, smart maintenance triage and CRA-ready exports. Paid plans from CA$35/month.
            </p>
            <div className="mt-7 inline-flex flex-wrap gap-3 justify-center">
              <Link to="/signup?role=landlord" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-7 py-3.5 rounded-full transition-colors">
                Choose a plan <ArrowRight className="w-4 h-4"/>
              </Link>
              <Link to="/" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-7 py-3.5 rounded-full transition-colors border border-white/10">
                Visit Central Rentals <ExternalLink className="w-4 h-4"/>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
